import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';
import { translations, LINKS, NIMNY_AVATAR, getNimnyAppUrl } from '../../data/translations';

const HeaderNew = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);
  const mobileLangRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside desktop lang dropdown
      if (langRef.current && langRef.current.contains(event.target)) {
        return;
      }
      // Check if click is inside mobile lang dropdown
      if (mobileLangRef.current && mobileLangRef.current.contains(event.target)) {
        return;
      }
      // Check if clicking on mobile language switcher button
      if (event.target.closest('[data-testid="language-switcher-mobile"]')) {
        return;
      }
      setIsLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsLangOpen(false);
    setIsMobileMenuOpen(false);
  };

  const trackClick = (buttonName) => {
    if (window.plausible) {
      window.plausible('Click', { props: { button: buttonName } });
    }
  };

  // Get URLs based on language
  const getAboutUrl = () => {
    if (language === 'ru') return '/ru/about-nimny';
    if (language === 'es') return '/es/about-nimny';
    return '/about-nimny';
  };

  const getCreateUrl = () => {
    if (language === 'ru') return 'https://nimny.app/ru/agent';
    if (language === 'es') return 'https://nimny.app/es/agent';
    return 'https://nimny.app/en/agent';
  };

  // Simplified nav items
  const navItems = {
    read: {
      en: 'Read',
      es: 'Leer',
      ru: 'Читать'
    }[language],
    create: {
      en: 'Create',
      es: 'Crear',
      ru: 'Создать'
    }[language],
    about: {
      en: 'About',
      es: 'Acerca',
      ru: 'О Нимни'
    }[language],
    amazon: 'Amazon'
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled
          ? 'bg-[#1a1a2e]/90 backdrop-blur-lg shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
      data-testid="header"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            <div 
              className={`w-9 h-9 rounded-full overflow-hidden border-2 transition-all duration-300 ${
                isScrolled ? 'border-amber-300/60' : 'border-amber-200/30'
              }`}
              style={{ backgroundColor: '#d4a574' }}
            >
              <img 
                src={NIMNY_AVATAR}
                alt="Nimny"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.2)', objectPosition: 'center 35%' }}
              />
            </div>
            <span className={`font-sans text-lg md:text-xl transition-colors ${
              isScrolled ? 'text-[#f5ebe0]/90' : 'text-amber-100/80'
            }`}>
              Nimny
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Read */}
            <a
              href={getNimnyAppUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Read - Header')}
              className={`font-sans text-sm px-4 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                isScrolled ? 'text-amber-200/90' : 'text-amber-100/80'
              }`}
              data-testid="nav-read"
            >
              {navItems.read}
            </a>

            {/* Create */}
            <a
              href={getCreateUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Create - Header')}
              className={`font-sans text-sm px-4 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                isScrolled ? 'text-amber-200/90' : 'text-amber-100/80'
              }`}
              data-testid="nav-create"
            >
              {navItems.create}
            </a>

            {/* Amazon */}
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Amazon - Header')}
              className={`font-sans text-sm px-4 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                isScrolled ? 'text-amber-200/90' : 'text-amber-100/80'
              }`}
              data-testid="nav-amazon"
            >
              {navItems.amazon}
            </a>

            <Link
              to="/lab"
              className={`font-sans text-sm px-4 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                isScrolled ? 'text-amber-200/90' : 'text-amber-100/80'
              }`}
              data-testid="nav-nimny-lab"
            >
              Nimny Lab
            </Link>

            {/* About */}
            <Link
              to={getAboutUrl()}
              className={`font-sans text-sm px-4 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                isScrolled ? 'text-amber-200/90' : 'text-amber-100/80'
              }`}
              data-testid="nav-about"
            >
              {navItems.about}
            </Link>

            {/* Divider */}
            <div className="w-px h-5 bg-amber-200/20 mx-2" />

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center gap-1 font-sans text-sm px-3 py-2 rounded-full transition-all duration-300 hover:bg-[#F4E6C8]/10 ${
                  isScrolled ? 'text-amber-200/80' : 'text-amber-100/70'
                }`}
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                <span>{LANGUAGES[language].name}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangOpen && (
                <div className="absolute right-0 mt-2 py-2 w-40 bg-[#1a1a2e] border border-amber-200/20 rounded-xl shadow-xl z-[200]">
                  {Object.values(LANGUAGES).map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full px-4 py-2.5 text-left font-sans text-sm transition-colors hover:bg-amber-200/10 ${
                        language === lang.code ? 'text-amber-300 bg-amber-200/5' : 'text-amber-200/70'
                      }`}
                    >
                      {lang.name} - {lang.fullName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Language */}
            <button
              onClick={() => {
                setIsLangOpen(!isLangOpen);
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-1 text-amber-200/80 text-sm p-2"
              data-testid="language-switcher-mobile"
            >
              <Globe className="w-4 h-4" />
              <span>{LANGUAGES[language].name}</span>
            </button>
            
            {/* Hamburger */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
                setIsLangOpen(false);
              }}
              className="p-2 text-amber-200"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Language Dropdown */}
        {isLangOpen && (
          <div 
            ref={mobileLangRef}
            className="md:hidden absolute right-4 top-16 py-2 w-40 bg-[#1a1a2e] border border-amber-200/20 rounded-xl shadow-xl z-[300]"
            onClick={(e) => e.stopPropagation()}
          >
            {Object.values(LANGUAGES).map((lang) => (
              <button
                key={lang.code}
                data-testid={`mobile-lang-${lang.code}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLanguage(lang.code);
                  setIsLangOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left font-sans text-sm transition-colors hover:bg-amber-200/10 active:bg-amber-200/20 ${
                  language === lang.code ? 'text-amber-300 bg-amber-200/5' : 'text-amber-200/70'
                }`}
              >
                {lang.name} - {lang.fullName}
              </button>
            ))}
          </div>
        )}

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[400px] mt-4 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="flex flex-col gap-2 py-4 border-t border-amber-200/20 bg-[#1a1a2e]/95 rounded-b-xl px-2">
            <a
              href={getNimnyAppUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackClick('Read - Header Mobile');
                setIsMobileMenuOpen(false);
              }}
              className="font-sans text-amber-200/90 py-3 px-4 rounded-xl hover:bg-[#F4E6C8]/10 transition-all"
            >
              {navItems.read}
            </a>
            <a
              href={getCreateUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackClick('Create - Header Mobile');
                setIsMobileMenuOpen(false);
              }}
              className="font-sans text-amber-200/90 py-3 px-4 rounded-xl hover:bg-[#F4E6C8]/10 transition-all"
            >
              {navItems.create}
            </a>
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackClick('Amazon - Header Mobile');
                setIsMobileMenuOpen(false);
              }}
              className="font-sans text-amber-200/90 py-3 px-4 rounded-xl hover:bg-[#F4E6C8]/10 transition-all"
            >
              {navItems.amazon}
            </a>
            <Link
              to="/lab"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-sans text-amber-200/90 py-3 px-4 rounded-xl hover:bg-[#F4E6C8]/10 transition-all"
              data-testid="nav-nimny-lab-mobile"
            >
              Nimny Lab
            </Link>
            <Link
              to={getAboutUrl()}
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-sans text-amber-200/90 py-3 px-4 rounded-xl hover:bg-[#F4E6C8]/10 transition-all"
              data-testid="nav-about-mobile"
            >
              {navItems.about}
            </Link>
          </nav>
        </div>
      </div>

      {/* Overlay */}
      {(isMobileMenuOpen || isLangOpen) && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 z-[-1]"
          onClick={() => {
            setIsMobileMenuOpen(false);
            setIsLangOpen(false);
          }}
        />
      )}
    </header>
  );
};

export default HeaderNew;
